import { CalendarIcon, UserGroupIcon, PhotoIcon, PencilIcon, DocumentDuplicateIcon, EllipsisHorizontalIcon } from "@heroicons/react/20/solid";
import { CurrencyDollarIcon } from "@heroicons/react/24/outline";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { BUTTON_CLASSES, ICON_CLASSES, TABLE_HEADER_CLASSES, TABLE_CELL_CLASSES } from "./constants";
import { getEventStatus } from "../../utils/eventStatus";
import { CreateEventButton } from "./EventsToolbar";
import { buildEventDuplicateState } from "../utils/duplicateEvent";
import { variantURL, fallbackToOriginal } from "../../utils/imageVariant";

// Events Card View Component
export const EventsCardView = ({ events, loading, onSelectEvent, isSuperAdmin, navigate }) => (
  <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
    {loading ? (
      // Show skeleton cards while loading
      Array.from({ length: 3 }).map((_, index) => (
        <SkeletonCard key={`skeleton-${index}`} />
      ))
    ) : (
      events.map((event, index) => (
        <EventCard key={event.id} event={event} index={index} onSelect={onSelectEvent} isSuperAdmin={isSuperAdmin} navigate={navigate} />
      ))
    )}
  </div>
);

// Per-event actions, tucked behind a "..." so they don't compete with the event
// itself. Shared by the card and the table row so both offer the same actions.
//
// `anchor` renders the panel in a portal: the card is overflow-hidden to clip
// its photo corners and would otherwise cut the menu off. stopPropagation keeps
// the trigger from also opening the card's preview panel.
const EventActionsMenu = ({ event, navigate }) => (
  <Menu as="div" className="flex-shrink-0">
    <MenuButton
      title="Event actions"
      onClick={(e) => e.stopPropagation()}
      className="inline-flex items-center justify-center rounded-md border border-gray-200 dark:border-gray-700 p-1 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary"
    >
      <EllipsisHorizontalIcon className={ICON_CLASSES.MEDIUM} aria-hidden="true" />
    </MenuButton>
    <MenuItems
      anchor="bottom end"
      className="z-20 mt-2 w-48 rounded-md bg-white dark:bg-gray-800 py-1 shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-white/10 focus:outline-none"
    >
      <MenuItem>
        {({ focus }) => (
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate("/events/new", { state: buildEventDuplicateState(event) });
            }}
            className={`${focus ? "bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100" : "text-gray-700 dark:text-gray-200"} flex w-full items-center px-4 py-2 text-left text-sm`}
          >
            <DocumentDuplicateIcon className={`${ICON_CLASSES.SMALL} mr-2 text-gray-400 dark:text-gray-500`} />
            Duplicate
          </button>
        )}
      </MenuItem>
    </MenuItems>
  </Menu>
);

// Events Table View Component
export const EventsTableView = ({ events, navigate, onSelectEvent, isSuperAdmin }) => (
  <div className="mt-8">
    <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-800">
          <tr>
            <th scope="col" className={TABLE_HEADER_CLASSES}>
              Event Name
            </th>
            <th scope="col" className={TABLE_HEADER_CLASSES}>
              Date & Time
            </th>
            <th scope="col" className={TABLE_HEADER_CLASSES}>
              Status
            </th>
            <th scope="col" className={TABLE_HEADER_CLASSES}>
              Attendees
            </th>
            <th scope="col" className={TABLE_HEADER_CLASSES}>
              Host
            </th>
            <th scope="col" className={TABLE_HEADER_CLASSES}>
              Revenue
            </th>
            <th scope="col" className={TABLE_HEADER_CLASSES}>
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          {events.map((event) => (
            <EventTableRow key={event.id} event={event} navigate={navigate} onSelect={onSelectEvent} isSuperAdmin={isSuperAdmin} />
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// Empty State Component
export const EmptyState = ({ navigate, activeTab }) => {
  if (activeTab === "cancelled") {
    return (
      <div className="mt-8 text-center py-12">
        <CalendarIcon className={`mx-auto ${ICON_CLASSES.LARGE} text-gray-400 dark:text-gray-500`} />
        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No cancelled events</h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Events you cancel will show up here.</p>
      </div>
    );
  }
  return (
    <div className="mt-8 text-center py-12">
      <CalendarIcon className={`mx-auto ${ICON_CLASSES.LARGE} text-gray-400 dark:text-gray-500`} />
      <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No events</h3>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Get started by creating a new event.</p>
      <div className="mt-6 flex items-center justify-center gap-3">
        <CreateEventButton navigate={navigate} />
        {activeTab === "upcoming" && (
          <button
            type="button"
            onClick={() => navigate("/events/suggestions")}
            className={BUTTON_CLASSES.SECONDARY}
          >
            View Ideas
          </button>
        )}
      </div>
    </div>
  );
};

// Loading Indicator Component
export const LoadingIndicator = ({ loadingMore, observerRef }) => (
  <div ref={observerRef} className="mt-8 flex justify-center py-4">
    {loadingMore ? (
      <div className="flex items-center space-x-2 text-gray-500 dark:text-gray-400">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
        <span className="text-sm">Loading more events...</span>
      </div>
    ) : (
      <div className="h-4"></div> // Invisible trigger element
    )}
  </div>
);

// End of Results Component
export const EndOfResults = () => (
  <div className="mt-8 text-center py-4 text-gray-500 dark:text-gray-400 text-sm">
    You've reached the end of the events list
  </div>
);

// Skeleton Card Component
const SkeletonCard = () => (
  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden animate-pulse">
    <div className="h-48 bg-gray-200 dark:bg-gray-700"></div>
    <div className="p-4">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
          <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-300 mb-3">
            <div className="flex">
              <div className={`${ICON_CLASSES.SMALL} bg-gray-200 dark:bg-gray-700 rounded mr-1`}></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
            </div>
            <div className="flex">
              <div className={`${ICON_CLASSES.SMALL} bg-gray-200 dark:bg-gray-700 rounded mr-1`}></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-16"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Revenue lands a beat after the row paints — the list renders first and the
// getEventsRevenue callable merges in after (see useEventsData) — so the badge
// holds its place with a shimmer instead of popping in.
const RevenueSkeleton = () => (
  <span className="flex items-center animate-pulse" aria-hidden="true">
    <span className="h-3.5 w-3.5 rounded-full bg-gray-200 dark:bg-gray-700 mr-1"></span>
    <span className="h-3 w-10 rounded bg-gray-200 dark:bg-gray-700"></span>
  </span>
);

// Attendee Avatars Component
const AttendeeAvatars = ({ event, maxDisplay = 5, size = "h-7 w-7" }) => {
  const attendeeCount =
    (event.rsvpSitters?.length || 0) +
    (event.sitters?.length || 0) +
    (event.rsvpMovers?.length || 0) +
    (event.movers?.length || 0);
  const avatarIds = [...new Set([
    ...(event.sitters || []),
    ...(event.rsvpSitters || []),
    ...(event.movers || []),
    ...(event.rsvpMovers || []),
  ])];
  const eventUsers = event.eventUsers || {};
  const attendees = avatarIds
    .map(id => eventUsers[id])
    .filter(Boolean);
  const displayed = attendees.slice(0, maxDisplay);
  const remaining = attendeeCount - maxDisplay;

  if (attendeeCount === 0) return null;

  return (
    <div className="flex items-center">
      <div className="flex -space-x-2 overflow-hidden">
        {displayed.map((user, i) => (
          <img
            key={user.userID || i}
            src={variantURL(user.photoURL, 200)}
            onError={fallbackToOriginal(user.photoURL)}
            alt={user.name || ''}
            title={user.name || ''}
            className={`${size} rounded-full ring-2 ring-white dark:ring-gray-800 object-cover`}
          />
        ))}
        {remaining > 0 && (
          <span className={`${size} rounded-full ring-2 ring-white dark:ring-gray-800 bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-xs font-medium text-gray-600 dark:text-gray-300`}>
            +{remaining}
          </span>
        )}
      </div>
      <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">{attendeeCount}</span>
    </div>
  );
};

// Event Card Component
// Completed events render at full strength, like any other card. They aren't
// dead weight to skim past — a finished event is the one most likely to be
// duplicated into the next one, so it has to stay just as readable.
const EventCard = ({ event, index, onSelect, navigate }) => {
  const eventStatus = getEventStatus(event);
  return (
    <div
      key={event.id}
      onClick={() => onSelect(event)}
      className="bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 border border-gray-200 dark:border-gray-700 overflow-hidden cursor-pointer block"
    >
      {event.photos && event.photos.length > 0 && (
        <div className="relative h-48 bg-gray-100 dark:bg-gray-800">
          {/*
            GIS-228. The first card's cover photo IS the LCP element on this
            page, and its URL is unknowable until the events query resolves, so
            it starts late by construction. fetchpriority="high" at least stops
            the browser queueing it behind the rest of the grid; the others are
            lazy so they stop competing for connections.

            Explicit dimensions rather than intrinsic size: the frame is a fixed
            h-48 with object-cover, so declaring the box keeps a slow photo from
            shifting the card under the operator.

            GIS-246. The 800 variant covers that 384px box at DPR2. The
            original is the same picture at several times the bytes.
          */}
          <img
            src={variantURL(event.photos[0], 800)}
            onError={fallbackToOriginal(event.photos[0])}
            alt={event.name}
            width="384"
            height="192"
            fetchpriority={index === 0 ? "high" : "auto"}
            loading={index === 0 ? "eager" : "lazy"}
            decoding={index === 0 ? "sync" : "async"}
            className="w-full h-full object-cover"
          />
          <div className="absolute top-3 right-3">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${eventStatus.color}`}>
              {eventStatus.label}
            </span>
          </div>
          {event.photos.length > 1 && (
            <div className="absolute bottom-3 left-3 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded-full flex items-center">
              <PhotoIcon className={`${ICON_CLASSES.SMALL} mr-1`} />
              +{event.photos.length - 1}
            </div>
          )}
        </div>
      )}
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2 line-clamp-2">
              {event.name}
            </h3>
            <div className="flex items-center text-xs justify-between text-gray-600 dark:text-gray-300 mb-3">
              <div className="flex">
                <CalendarIcon className={`${ICON_CLASSES.SMALL} mr-1 text-gray-400 dark:text-gray-500`} />
                <span className="mr-2">{event.date.toDate().toLocaleDateString()} {event.date.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
              </div>
              <div className="flex">
                {event.hostname && (
                  <>
                    <UserGroupIcon className={`${ICON_CLASSES.SMALL} mr-1 text-gray-400 dark:text-gray-500`} />
                    <span>{event.hostname}</span>
                  </>
                )}
              </div>
            </div>
          </div>
          {!event.photos && (
            <div className="ml-4 flex-shrink-0">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${eventStatus.color}`}>
                {eventStatus.label}
              </span>
            </div>
          )}
        </div>
        <div className="flex items-center justify-between mt-2 gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <AttendeeAvatars event={event} maxDisplay={8} size="h-6 w-6" />
            {event.isCoHosted ? (
              <CoHostBadge />
            ) : event.revenuePending ? (
              <RevenueSkeleton />
            ) : event.revenue?.total > 0 ? (
              <span className="flex items-center text-xs text-green-700 dark:text-green-400 font-medium">
                <CurrencyDollarIcon className="h-3.5 w-3.5 mr-0.5" />
                {event.revenue.total.toFixed(2)}
              </span>
            ) : null}
          </div>
          {/* A copy would be created under the host's name, which a co-host can't do. */}
          {!event.isCoHosted && <EventActionsMenu event={event} navigate={navigate} />}
        </div>
      </div>
    </div>
  );
};

// GIS-222. Marks an event the signed-in host co-hosts rather than owns. It sits
// where the revenue badge would, since a co-host never sees what an event took in.
const CoHostBadge = () => (
  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
    Co-host
  </span>
);

// Event Table Row Component
const EventTableRow =({ event, navigate, onSelect, isSuperAdmin }) => {
  const eventStatus = getEventStatus(event);
  return (
    <tr key={event.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer" onClick={() => onSelect(event)}>
      <td className={TABLE_CELL_CLASSES}>
        <div className="flex items-center">
          {event.photos && event.photos.length > 0 && (
            <img
              src={variantURL(event.photos[0], 400)}
              onError={fallbackToOriginal(event.photos[0])}
              alt={event.name}
              className="h-10 w-10 rounded-lg object-cover mr-3"
            />
          )}
          <div>
            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {event.name}
              {event.isCoHosted && <span className="ml-2 align-middle"><CoHostBadge /></span>}
            </div>
            {event.photos && event.photos.length > 1 && (
              <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                <PhotoIcon className={`${ICON_CLASSES.SMALL} mr-1`} />
                +{event.photos.length - 1} photos
              </div>
            )}
          </div>
        </div>
      </td>
      <td className={`${TABLE_CELL_CLASSES} text-sm text-gray-900 dark:text-gray-100`}>
        <div className="flex items-center">
          <CalendarIcon className={`${ICON_CLASSES.SMALL} mr-2 text-gray-400 dark:text-gray-500`} />
          {event.date.toDate().toLocaleDateString()} {event.date.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </div>
      </td>
      <td className={TABLE_CELL_CLASSES}>
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${eventStatus.color}`}>
          {eventStatus.label}
        </span>
      </td>
      <td className={TABLE_CELL_CLASSES}>
        <AttendeeAvatars event={event} maxDisplay={4} size="h-6 w-6" />
      </td>
      <td className={`${TABLE_CELL_CLASSES} text-sm text-gray-900 dark:text-gray-100`}>
        {event.hostname && (
          <div className="flex items-center">
            <UserGroupIcon className={`${ICON_CLASSES.SMALL} mr-2 text-gray-400 dark:text-gray-500`} />
            {event.hostname}
          </div>
        )}
      </td>
      <td className={`${TABLE_CELL_CLASSES} text-sm text-gray-900 dark:text-gray-100`}>
        {event.revenuePending ? (
          <RevenueSkeleton />
        ) : event.revenue?.total > 0 ? (
          <div>
            <div className="flex items-center font-medium text-green-700 dark:text-green-400">
              <CurrencyDollarIcon className={`${ICON_CLASSES.SMALL} mr-1`} />
              {event.revenue.total.toFixed(2)}
            </div>
            {isSuperAdmin && event.revenue.tickets > 0 && (
              <div className="text-xs text-gray-400 dark:text-gray-500">Tickets: ${event.revenue.tickets.toFixed(2)}</div>
            )}
            {isSuperAdmin && event.revenue.prioritizations > 0 && (
              <div className="text-xs text-gray-400 dark:text-gray-500">Boosts: ${event.revenue.prioritizations.toFixed(2)}</div>
            )}
          </div>
        ) : (
          <span className="text-gray-400 dark:text-gray-500">—</span>
        )}
      </td>
      <td className={`${TABLE_CELL_CLASSES} text-sm font-medium`}>
        <div className="flex items-center gap-4">
          <button
            onClick={(e) => { e.stopPropagation(); navigate(`/events/${event.id}/edit`); }}
            className="text-primary hover:text-primaryDark flex items-center"
          >
            <PencilIcon className={`${ICON_CLASSES.SMALL} mr-1`} />
            Edit
          </button>
          {!event.isCoHosted && <EventActionsMenu event={event} navigate={navigate} />}
        </div>
      </td>
    </tr>
  );
};
